import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  Tooltip,
  IconButton,
  CircularProgress,
  Avatar,
  Fade,
  Skeleton
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Settings as SettingsIcon,
  TableChart as TableIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import PageHeader from '../components/common/PageHeader';
import AlertMessage from '../components/common/AlertMessage';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { AppDispatch } from '../store';
import { DataTable } from '../types/table.types';
import { TableService } from '../services/table.service';
import { selectUser } from '../store/slices/authSlice';

export default function TableList() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector(selectUser);

  const [tables, setTables] = useState<DataTable[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [tableToDelete, setTableToDelete] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);

  const isAdmin = user?.roles.some(role => role.name === 'ADMIN');

  useEffect(() => {
    setMounted(true);
    fetchTables();
  }, []);

  const fetchTables = async () => {
    try {
      setLoading(true);
      const data = await TableService.getAllTables();
      setTables(data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка загрузки таблиц');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTable = () => {
    navigate('/tables/create');
  };

  const handleEditTable = (id: string) => {
    navigate(`/tables/${id}/edit`);
  };

  const handleViewTable = (id: string) => {
    navigate(`/tables/${id}`);
  };

  const handleViewPermissions = (id: string) => {
    navigate(`/tables/${id}/permissions`);
  };

  const handleDeleteTable = (id: string) => {
    setTableToDelete(id);
    setDeleteDialog(true);
  };

  const confirmDeleteTable = async () => {
    if (!tableToDelete) return;

    try {
      await TableService.deleteTable(tableToDelete);
      setTables(tables.filter(table => table.id !== tableToDelete));
      setDeleteDialog(false);
      setTableToDelete(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка удаления таблицы');
    }
  };

  const cancelDeleteTable = () => {
    setDeleteDialog(false);
    setTableToDelete(null);
  };

  return (
    <Box>
      {/* Header with Gradient */}
      <Fade in={mounted} timeout={400}>
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 3,
            p: 4,
            mb: 4,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-50%',
              right: '-20%',
              width: '400px',
              height: '400px',
              background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
            }
          }}
        >
          <Box sx={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 64, height: 64 }}>
                <TableIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Таблицы данных
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Управление таблицами и доступом к данным
                </Typography>
              </Box>
            </Box>

            {isAdmin && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateTable}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  px: 3,
                  py: 1.5,
                  fontSize: '1rem',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.3)',
                  }
                }}
              >
                Создать таблицу
              </Button>
            )}
          </Box>
        </Box>
      </Fade>

      {loading ? (
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2, mb: 2 }} />
                  <Skeleton width="60%" sx={{ mb: 1 }} />
                  <Skeleton width="40%" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : tables.length === 0 ? (
        <Fade in timeout={600}>
          <Box sx={{
            mt: 8,
            textAlign: 'center',
            p: 6,
            borderRadius: 3,
            bgcolor: 'background.paper'
          }}>
            <Avatar sx={{
              width: 120,
              height: 120,
              margin: '0 auto',
              mb: 3,
              bgcolor: 'rgba(102, 126, 234, 0.1)'
            }}>
              <TableIcon sx={{ fontSize: 60, color: '#667eea' }} />
            </Avatar>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Таблицы не найдены
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Начните создавать таблицы для управления вашими данными
            </Typography>
            {isAdmin && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateTable}
                size="large"
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  px: 4,
                  py: 1.5
                }}
              >
                Создать первую таблицу
              </Button>
            )}
          </Box>
        </Fade>
      ) : (
        <Grid container spacing={3}>
          {tables.map((table, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={table.id}>
              <Fade in={mounted} timeout={600 + index * 100}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, pt: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: 'rgba(102, 126, 234, 0.1)',
                          color: '#667eea',
                          width: 48,
                          height: 48,
                          flexShrink: 0,
                        }}
                      >
                        <DescriptionIcon />
                      </Avatar>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="h6"
                          component="h2"
                          sx={{
                            fontWeight: 600,
                            mb: 0.5,
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                            hyphens: 'auto',
                          }}
                        >
                          {table.name}
                        </Typography>

                        {table.description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mb: 2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflowWrap: 'anywhere',
                            }}
                          >
                            {table.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        icon={<TableIcon />}
                        label={`${table.columns.length} ${table.columns.length === 1 ? 'колонка' :
                          table.columns.length > 1 && table.columns.length < 5 ? 'колонки' : 'колонок'}`}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(102, 126, 234, 0.1)',
                          color: '#667eea',
                          fontWeight: 600
                        }}
                      />
                    </Box>
                  </CardContent>

                  <CardActions
                    sx={{
                      justifyContent: 'space-between',
                      p: 2,
                      pt: 0,
                      borderTop: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Button
                      variant="text"
                      startIcon={<ViewIcon />}
                      onClick={() => handleViewTable(table.id)}
                      sx={{
                        color: '#667eea',
                        '&:hover': {
                          bgcolor: 'rgba(102, 126, 234, 0.08)',
                        }
                      }}
                    >
                      Просмотр
                    </Button>

                    <Box>
                      {isAdmin && (
                        <>
                          <Tooltip title="Редактировать таблицу">
                            <IconButton
                              onClick={() => handleEditTable(table.id)}
                              sx={{
                                color: '#667eea',
                                '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.08)' }
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Настройки доступа">
                            <IconButton
                              onClick={() => handleViewPermissions(table.id)}
                              sx={{
                                color: '#667eea',
                                '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.08)' }
                              }}
                            >
                              <SettingsIcon />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Удалить таблицу">
                            <IconButton
                              color="error"
                              onClick={() => handleDeleteTable(table.id)}
                              sx={{
                                '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.08)' }
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </CardActions>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>
      )}

      <AlertMessage
        open={!!error}
        message={error || ''}
        severity="error"
        onClose={() => setError(null)}
      />

      <ConfirmDialog
        open={deleteDialog}
        title="Удаление таблицы"
        message="Вы уверены, что хотите удалить эту таблицу? Это действие нельзя отменить, и все данные будут потеряны."
        confirmText="Удалить"
        cancelText="Отмена"
        onConfirm={confirmDeleteTable}
        onCancel={cancelDeleteTable}
        severity="error"
      />
    </Box>
  );
}